import {ReactNode} from 'react';
import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import {fonts, kit, radius} from './tokens';

type TrustCardVariant = 'bar' | 'rail';

interface TrustCardProps {
  // 'bar' -- a quiet inline row (e.g. under a list of quotes in the main
  // column). 'rail' (default) -- a boxed green-tint card for the right rail.
  variant?: TrustCardVariant;
  // Supports an embedded <b> lead-in, e.g. <>&nbsp;<b>Payments are held in
  // trust</b> until you approve the work.</>
  message: ReactNode;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
}

// The shield/lock glyph used identically for every trust message across the
// approved mockups -- fixed brand chrome, not per-instance data.
const ShieldIcon = ({size}: {size: number}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2.5" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Green-tint "payments are held in trust" message, in the two variants used
// throughout the redesign: a quiet inline `bar` (web port of `.trustline`)
// or a boxed `rail` card (web port of `.trustcard`).
const TrustCard = ({variant = 'rail', message, icon, sx}: TrustCardProps) => {
  if (variant === 'bar') {
    return (
      <Box
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            marginTop: '14px',
            padding: '0 4px',
            fontSize: 13,
            fontWeight: 500,
            color: kit.slate,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}>
        <Box sx={{color: kit.green, display: 'flex', flexShrink: 0}}>
          {icon ?? <ShieldIcon size={15} />}
        </Box>
        {message}
      </Box>
    );
  }

  return (
    <Box
      sx={[
        {
          backgroundColor: kit.greenTint,
          border: `1px solid ${kit.greenTint}`,
          borderRadius: `${radius.lg}px`,
          padding: '16px 18px',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: `${radius.sm}px`,
          backgroundColor: '#FFFFFF',
          color: kit.green,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
        {icon ?? <ShieldIcon size={18} />}
      </Box>
      <Box
        sx={{
          fontFamily: fonts.sans,
          fontSize: 13.5,
          lineHeight: 1.45,
          color: kit.inkSoft,
          fontWeight: 500,
          paddingTop: '1px',
          '& b': {color: kit.ink, fontWeight: 700},
        }}>
        {message}
      </Box>
    </Box>
  );
};

export default TrustCard;
